import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiCreditCard, FiCheckCircle, FiInfo, FiChevronRight, 
  FiShoppingBag, FiShield, FiX, FiActivity, FiSearch,
  FiFilter, FiCalendar
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getOfferings, getFlatCategories, verifyMockPayment } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { isFutureFinancialYear } from '../../utils/financialYears';
import logger from '../../utils/logger';
import styles from './PayOffersPage.module.css';

import { fmtCurrency } from '../../utils/formatNumber';

const getOfferingMemberName = (offering) =>
  offering.member?.name
  || offering.creator?.name
  || offering.userId?.name
  || '—';

export default function PayOffersPage() {
  const { user, activeDerasarId, activeDerasar, visibleFinancialYears, setActiveFinancialYearId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [offerings, setOfferings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [payAmounts, setPayAmounts] = useState({}); // { offeringId: amount }
  const [showModal, setShowModal] = useState(false);
  const [paying, setPaying] = useState(false);
  const [successReceipt, setSuccessReceipt] = useState(null);
  const [narration, setNarration] = useState('');
  const [receiptUserId, setReceiptUserId] = useState('');

  // Filter states
  const [categories, setCategories] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  useEffect(() => {
    if (!loading && activeDerasar && !activeDerasar.derasarId?.onlinePaymentEnabled) {
      toast.error('Online payment is not enabled for this Derasar');
      navigate('/accounting/offerings');
    }
  }, [loading, activeDerasar, navigate]);

  // Load pending offerings for this member
  useEffect(() => {
    if (!activeDerasarId || !user?.id) return;
    if (activeDerasar && !activeDerasar.derasarId?.onlinePaymentEnabled) return;
    setLoading(true);
    getOfferings({ derasarId: activeDerasarId })
      .then((res) => {
        // Filter for pending/partial ones
        const pending = (res.data || []).filter(o => 
          o.status === 'pending' || o.status === 'partial'
        ).sort((a, b) => new Date(a.offeringDate) - new Date(b.offeringDate));

        const payable = pending.filter((offering) => {
          const financialYearId = String(offering.financialYearId?.id ?? offering.financialYearId ?? '');
          const fy = visibleFinancialYears.find((year) => String(year.id) === financialYearId);
          return !fy || !isFutureFinancialYear(fy);
        });
        
        setOfferings(payable);
        
        // Initialize pay amounts with full pending balance
        const initialAmounts = {};
        payable.forEach(o => {
          initialAmounts[o.id] = (o.offeredAmount - o.paidAmount).toFixed(2);
        });
        setPayAmounts(initialAmounts);

        // Handle pre-selected IDs from navigation state (e.g., from New Offering)
        if (location.state?.preselectedIds) {
          const validPreselected = location.state.preselectedIds
            .map(id => String(id))
            .filter(id => payable.some(o => String(o.id) === id));
            
          if (validPreselected.length > 0) {
            setSelectedIds(validPreselected);
          } else if (location.state.preselectedIds.length > 0) {
            toast.info('Future-year offers cannot be paid yet.');
          }
        }
      })
      .catch(() => toast.error('Failed to load offerings'))
      .finally(() => setLoading(false));

    // Load categories for filter
    getFlatCategories(activeDerasarId)
      .then(res => setCategories(res.data || []))
      .catch((err) => {
        logger.warn('Failed to load pay-offers categories', {
          message: err?.message,
          derasarId: activeDerasarId,
        });
      });
  }, [activeDerasarId, user?.id, location.state, activeDerasar, visibleFinancialYears]);

  const filteredOfferings = useMemo(() => {
    return offerings.filter(o => {
      // Name filter
      if (filterName && !getOfferingMemberName(o).toLowerCase().includes(filterName.toLowerCase())) return false;
      
      // Category filter
      if (filterCat && String(o.donationCategoryId) !== filterCat) return false;
      
      // Date range filter
      if (filterDateFrom && new Date(o.offeringDate) < new Date(filterDateFrom)) return false;
      if (filterDateTo && new Date(o.offeringDate) > new Date(filterDateTo)) return false;
      
      return true;
    });
  }, [offerings, filterName, filterCat, filterDateFrom, filterDateTo]);

  const isAllSelected = filteredOfferings.length > 0 && 
    filteredOfferings.every(o => selectedIds.includes(String(o.id)));

  const toggleAll = () => {
    if (isAllSelected) {
      // Remove all visible IDs from selection
      const filteredIds = filteredOfferings.map(o => String(o.id));
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Add all visible IDs to selection
      const newIds = [...new Set([...selectedIds, ...filteredOfferings.map(o => String(o.id))])];
      setSelectedIds(newIds);
    }
  };

  const toggleSelect = (id) => {
    const sid = String(id);
    setSelectedIds(prev => 
      prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]
    );
  };

  const selectedOfferings = useMemo(() => 
    offerings.filter(o => selectedIds.includes(String(o.id))), 
  [offerings, selectedIds]);

  const displayMembers = useMemo(() => {
    const list = selectedOfferings
      .map((offering) => ({
        id: String(offering.userId?.id ?? offering.userId ?? ''),
        name: getOfferingMemberName(offering),
      }))
      .filter((item) => item.id);

    const unique = [];
    const map = new Map();
    for (const item of list) {
      if (!map.has(item.id)) {
        map.set(item.id, true);
        unique.push(item);
      }
    }
    return unique;
  }, [selectedOfferings]);

  const totalToPay = useMemo(() => {
    return selectedIds.reduce((sum, id) => sum + Number(payAmounts[id] || 0), 0);
  }, [selectedIds, payAmounts]);

  useEffect(() => {
    if (displayMembers.length > 0) {
      const currentInList = displayMembers.find((m) => m.id === String(user?.id ?? ''));
      setReceiptUserId(currentInList ? currentInList.id : displayMembers[0].id);
    }
  }, [displayMembers, user?.id]);

  const handlePayAmountChange = (id, val) => {
    setPayAmounts(prev => ({ ...prev, [id]: val }));
  };

  const handleProceed = () => {
    if (selectedIds.length === 0) return toast.error('Select at least one offering to pay');
    if (totalToPay <= 0) return toast.error('Amount must be greater than zero');
    setShowModal(true);
  };

  const handleMockSuccess = async () => {
    setPaying(true);
    try {
      const settlementData = {
        derasarId: activeDerasarId,
        financialYearId: selectedOfferings[0].financialYearId, // Assuming all in same FY for simplicity
        amountInr: totalToPay,
        offeringSettlements: selectedOfferings.map(o => ({
          offeringId: o.id,
          settledAmount: Number(payAmounts[o.id])
        })),
        narration: narration || `Online payment for ${selectedIds.length} offerings`,
        receiptUserId: displayMembers.length > 1 ? receiptUserId : undefined,
      };

      const res = await verifyMockPayment(settlementData);
      if (res.data?.receipt?.financialYearId) {
        setActiveFinancialYearId(res.data.receipt.financialYearId);
      }
      setSuccessReceipt(res.data.receipt);
      toast.success('Payment successful!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  if (successReceipt) {
    return (
      <div className={styles.page}>
        <div className={styles.modal} style={{ margin: '40px auto' }}>
          <div className={styles.modalBody} style={{ paddingTop: '40px' }}>
            <div className={styles.successIcon}><FiCheckCircle size={32} /></div>
            <h2 className={styles.modalTitle}>Payment Rooted!</h2>
            <p className={styles.modalText}>
              Your payment of <strong>{fmtCurrency(successReceipt.amountInr)}</strong> has been successfully processed.
              Receipt <strong>#{successReceipt.receiptNo}</strong> has been generated.
            </p>
            <div className={styles.paymentSummary}>
              <div className={styles.summaryRow}>
                <span>Receipt No</span>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ padding: '2px 8px', fontWeight: 700 }}
                  onClick={() => navigate(`/accounting/receipts/${successReceipt.id}`)}
                >
                  {successReceipt.receiptNo} ?
                </button>
              </div>
              <div className={styles.summaryRow}>
                <span>Date</span>
                <strong>{new Date().toLocaleDateString('en-IN')}</strong>
              </div>
            </div>
          </div>
          <div className={styles.modalFooter}>
            <button
              className={`${styles.fullWidthBtn} btn btn-primary`}
              onClick={() => {
                if (successReceipt?.financialYearId) {
                  setActiveFinancialYearId(successReceipt.financialYearId);
                }
                navigate('/accounting/receipts');
              }}
            >
              View My Receipts
            </button>
            <button className={`${styles.fullWidthBtn} btn btn-ghost`} onClick={() => window.location.reload()}>
              Pay More Offers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2>Pay Offerings</h2>
        <p>Select the offerings you would like to settle online via Razorpay.</p>
      </div>

      {offerings.length === 0 ? (
        <div className={styles.emptyState}>
          <FiShoppingBag className={styles.emptyIcon} />
          <p>You have no pending or partial offerings at {activeDerasar?.derasarId?.name || 'this Derasar'}.</p>
          <button className="btn btn-primary" style={{ marginTop: '20px' }} onClick={() => navigate('/accounting/offerings')}>
            Create New Offering
          </button>
        </div>
      ) : (
        <>
          <div className={styles.filterBar}>
            <div className={styles.filterGroup}>
              <div className={styles.searchWrap}>
                <FiSearch className={styles.searchIcon} />
                <input 
                  type="text" 
                  placeholder="Search by name..." 
                  className={styles.filterInput}
                  value={filterName}
                  onChange={e => setFilterName(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.filterGroup}>
              <div className={styles.filterField}>
                <FiFilter className={styles.fieldIcon} />
                <select 
                  className={styles.filterSelect}
                  value={filterCat}
                  onChange={e => setFilterCat(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={`${styles.filterGroup} ${styles.filterGroupRange}`}>
              <div className={styles.rangeField}>
                <FiCalendar className={styles.fieldIcon} />
                <input 
                  type="date" 
                  className={styles.filterDate}
                  value={filterDateFrom}
                  onChange={e => setFilterDateFrom(e.target.value)}
                  placeholder="From"
                />
                <span className={styles.rangeSep}>to</span>
                <input 
                  type="date" 
                  className={styles.filterDate}
                  value={filterDateTo}
                  onChange={e => setFilterDateTo(e.target.value)}
                  placeholder="To"
                />
              </div>
            </div>
          </div>

          <div className={styles.bulkActions}>
            <button className={`${styles.selectAllBtn} btn btn-ghost`} onClick={toggleAll}>
              <div className={`${styles.fakeCheckbox} ${isAllSelected ? styles.checked : ''}`}>
                {isAllSelected && <FiCheckCircle size={12} />}
              </div>
              {isAllSelected ? 'Deselect All Entries' : 'Select All Entries'} ({filteredOfferings.length})
            </button>
            
            {(filterName || filterCat || filterDateFrom || filterDateTo) && (
              <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => {
                setFilterName('');
                setFilterCat('');
                setFilterDateFrom('');
                setFilterDateTo('');
              }}>
                Clear Filters
              </button>
            )}
          </div>
          <div className={styles.card}>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.checkboxCell}></th>
                    <th>Member</th>
                    <th>Date</th>
                    <th>Donation Category</th>
                    <th className={styles.amtCell}>Offered</th>
                    <th className={styles.amtCell}>Pending</th>
                    <th className={styles.payCell}>Amount to Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOfferings.map(o => {
                    const isSelected = selectedIds.includes(String(o.id));
                    const pending = o.offeredAmount - o.paidAmount;
                    const memberName = getOfferingMemberName(o);
                    return (
                      <tr key={o.id} className={isSelected ? styles.rowSelected : ''}>
                        <td className={styles.checkboxCell}>
                          <input 
                            type="checkbox" 
                            className={styles.checkbox} 
                            checked={isSelected}
                            onChange={() => toggleSelect(o.id)}
                          />
                        </td>
                        <td>
                          <div className={styles.memberName}>{memberName}</div>
                        </td>
                        <td className={styles.dateCell}>
                          {formatDate(o.offeringDate)}
                        </td>
                        <td>
                          <div className={styles.catCell}>
                            <span className={styles.catName}>{o.DonationCategory?.name || 'General'}</span>
                          </div>
                        </td>
                        <td className={styles.amtCell}>{fmtCurrency(o.offeredAmount)}</td>
                        <td className={styles.amtCell} style={{ color: 'var(--color-primary)', fontWeight: 700 }}>{fmtCurrency(pending)}</td>
                        <td className={styles.payCell}>
                          <div className={styles.payInputWrap}>
                            <span className={styles.currencyPrefix}>?</span>
                            <input 
                              type="number" 
                              className={styles.payInput}
                              value={payAmounts[o.id] || ''}
                              disabled={!isSelected}
                              onChange={(e) => handlePayAmountChange(o.id, e.target.value)}
                              max={pending}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className={styles.footer}>
              <div className={styles.summaryInfo}>
                <span className={styles.selectedCount}>{selectedIds.length} offering(s) selected</span>
                <span className={styles.totalAmountLabel}>
                  Total to Pay: <span className={styles.totalAmountValue}>{fmtCurrency(totalToPay)}</span>
                </span>
              </div>
              <button 
                className={`btn btn-primary ${styles.payBtn}`}
                disabled={selectedIds.length === 0 || totalToPay <= 0}
                onClick={handleProceed}
              >
                Pay Now <FiChevronRight />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Helper Info */}
      <div className={styles.infoBox}>
        <FiInfo size={20} className={styles.infoIcon} />
        <div className={styles.infoText}>
          <strong>Secure Payments:</strong> Your payments are processed securely via Razorpay. 
          Upon successful payment, a digital receipt will be generated instantly and linked to your offering.
          Partial payments are allowed if you wish to settle an offering in multiple installments.
        </div>
      </div>

      {/* Mock Payment Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalIcon}><FiCreditCard size={28} /></div>
              <button className={styles.modalClose} onClick={() => setShowModal(false)} style={{ position: 'absolute', right: '20px', top: '24px', border:'none', background:'none', cursor:'pointer' }}>
                <FiX size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <h3 className={styles.modalTitle}>Confirm Payment</h3>
              <p className={styles.modalText}>You are about to pay <strong>{fmtCurrency(totalToPay)}</strong> for {selectedIds.length} offering(s) at {activeDerasar?.derasarId?.name}.</p>
              
              <div className={styles.paymentSummary}>
                <div className={styles.summaryRow}><span>Subtotal</span><strong>{fmtCurrency(totalToPay)}</strong></div>
                <div className={styles.summaryRow}><span>Platform Fee</span><strong>?0.00</strong></div>
                <div className={styles.summaryTotal}><span>Amount Payable</span><strong>{fmtCurrency(totalToPay)}</strong></div>
              </div>

              <div className={styles.recipientBox}>
                <label>Member</label>
                <p className={styles.recipientHint}>
                  {displayMembers.length > 1
                    ? 'These member names are included in the selected offerings.'
                    : 'This payment is being made for the following member.'}
                </p>
                <div className={styles.payByList}>
                  {displayMembers.map((member) => (
                    <div key={member.id} className={styles.payByItem}>
                      {member.name}
                    </div>
                  ))}
                </div>
              </div>

              {displayMembers.length > 1 && (
                <div className={styles.recipientBox}>
                  <label>Receipt Holder</label>
                  <p className={styles.recipientHint}>Multiple members selected. Who should be named on the receipt?</p>
                  <select 
                    className={styles.recipientSelect}
                    value={receiptUserId}
                    onChange={e => setReceiptUserId(e.target.value)}
                  >
                    {displayMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.narrationBox}>
                <label>Add a note (optional)</label>
                <input 
                  type="text" 
                  className={styles.narrationInput}
                  placeholder="e.g. Monthly maintenance"
                  value={narration}
                  onChange={e => setNarration(e.target.value)}
                />
              </div>
            </div>
            
            <div className={styles.modalFooter}>
              <button 
                className={`${styles.fullWidthBtn} btn btn-primary`} 
                onClick={handleMockSuccess}
                disabled={paying}
              >
                {paying ? 'Processing...' : (
                  <><FiShield /> Complete Payment (Mock)</>
                )}
              </button>
              <button className={`${styles.fullWidthBtn} btn btn-ghost`} onClick={() => setShowModal(false)} disabled={paying}>
                Go Back
              </button>
            </div>
            
            <div style={{ textAlign: 'center', padding: '0 32px 20px', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
              <FiActivity size={10} /> This is a simulated Razorpay checkout environment.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
